import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import { toastError } from "../../shared/toast";
import { Pill, SourcePill, errMsg, fmtDue, overdueLabel } from "../common";
import { PIPE_LABEL, type KCard, type Pipe } from "./common";

type Board = { pipe: Pipe; stages: string[]; columns: Record<string, KCard[]> };

// Quadro v2: colunas = etapas do pipe; arrastar entre colunas move (POST /stage).
// Última coluna é o "concluído". ponytail: DnD nativo HTML5, sem lib.
export function KanbanBoard({ pipe, onPipe, onOpen }: { pipe: Pipe; onPipe: (p: Pipe) => void; onOpen: (id: string) => void }) {
  const qc = useQueryClient();
  const [dragId, setDragId] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);

  const board = useQuery({
    queryKey: ["kanban", "board", pipe],
    queryFn: () => api.get<Board>(`/kanban/board?pipe=${pipe}`),
    refetchInterval: 60_000,
  });
  const move = useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: string }) => api.post(`/kanban/${id}/stage`, { stage }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["kanban"] }),
    onError: e => toastError(errMsg(e, "Não foi possível mover o card.")),
  });

  const stages = board.data?.stages ?? [];
  const cols = board.data?.columns ?? {};

  function drop(stage: string) {
    const cur = dragId && Object.values(cols).flat().find(c => c.id === dragId);
    if (cur && cur.stage !== stage) move.mutate({ id: dragId!, stage });
    setDragId(null);
    setOver(null);
  }

  return (
    <div style={{ flex: 1, overflow: "auto", padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        {(Object.keys(PIPE_LABEL) as Pipe[]).map(p => (
          <button key={p} onClick={() => onPipe(p)} style={{ padding: "6px 16px", borderRadius: 99, fontSize: 13, cursor: "pointer", fontWeight: pipe === p ? 600 : 400, background: pipe === p ? "var(--brand-soft)" : "var(--surface-hover)", border: `1px solid ${pipe === p ? "var(--brand)" : "var(--line)"}`, color: pipe === p ? "var(--brand)" : "var(--ink-secondary)" }}>
            {PIPE_LABEL[p]}
          </button>
        ))}
        <span style={{ fontSize: 12, color: "var(--ink-muted)", marginLeft: 8 }}>Arraste um card para mudar a etapa.</span>
        {board.isError && <span style={{ fontSize: 12, color: "var(--danger)" }}>Não foi possível carregar o quadro.</span>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.max(stages.length, 1)}, minmax(240px, 1fr))`, gap: 14, alignItems: "start", overflowX: "auto" }}>
        {stages.map((stage, i) => {
          const last = i === stages.length - 1;
          const cards = cols[stage] ?? [];
          const hot = over === stage && !!dragId;
          return (
            <div
              key={stage}
              onDragOver={e => { e.preventDefault(); if (over !== stage) setOver(stage); }}
              onDragLeave={() => setOver(o => (o === stage ? null : o))}
              onDrop={e => { e.preventDefault(); drop(stage); }}
              style={{ background: hot ? "var(--brand-soft)" : "var(--surface-hover)", border: `1px ${hot ? "dashed var(--brand)" : "solid var(--line)"}`, borderRadius: 12, padding: 10, display: "flex", flexDirection: "column", gap: 8, minWidth: 0, minHeight: 120, transition: "background .12s" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 4px 4px" }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: last ? "var(--ok)" : "var(--ink)" }}>{stage}</span>
                <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>{cards.length}</span>
              </div>
              {cards.map(c => (
                <Card key={c.id} c={c} done={last} dragging={dragId === c.id} onOpen={onOpen} onDragStart={() => setDragId(c.id)} onDragEnd={() => { setDragId(null); setOver(null); }} />
              ))}
              {!cards.length && !board.isLoading && (
                <div style={{ fontSize: 12, color: "var(--ink-dim)", padding: "14px 4px", textAlign: "center" }}>Nada aqui.</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Card({ c, done, dragging, onOpen, onDragStart, onDragEnd }: {
  c: KCard; done: boolean; dragging: boolean; onOpen: (id: string) => void; onDragStart: () => void; onDragEnd: () => void;
}) {
  const tone = done
    ? { bg: "var(--ok-soft)", line: "var(--ok-line)", due: "var(--ok)" }
    : c.overdue
      ? { bg: "var(--danger-soft)", line: "var(--danger-line)", due: "var(--danger)" }
      : { bg: "var(--surface-card)", line: "var(--line)", due: "var(--ink-secondary)" };
  const people = [c.owner?.name ?? "—", ...c.participants.map(p => p.name)];
  return (
    <div
      role="button"
      tabIndex={0}
      draggable
      onDragStart={e => { e.dataTransfer.effectAllowed = "move"; onDragStart(); }}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(c.id)}
      onKeyDown={e => { if (e.key === "Enter") onOpen(c.id); }}
      style={{ textAlign: "left", width: "100%", boxSizing: "border-box", background: tone.bg, border: `1px solid ${tone.line}`, borderRadius: 10, padding: 12, cursor: "grab", display: "flex", flexDirection: "column", gap: 8, opacity: dragging ? 0.5 : 1 }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
        {c.tipo_label && <Pill color="var(--violet)" bg="var(--violet-soft)" line="var(--violet-soft)">{c.tipo_label}</Pill>}
        {c.subject && <Pill color="var(--info)" bg="var(--info-soft)" line="var(--info-line)">{c.subject.name}</Pill>}
        {c.overdue && !done && c.due_date && <Pill color="var(--danger)" bg="var(--surface-card)" line="var(--danger-line)">{overdueLabel(c.due_date, c.due_time)}</Pill>}
        {c.source === "api" && <SourcePill t={c} />}
      </div>
      <div style={{ fontSize: 13.5, fontWeight: 500, color: "var(--ink)", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
        {c.title}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: "var(--ink-muted)", flexWrap: "wrap" }}>
        {c.agent && <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 140 }} title={c.agent.name}>{c.agent.name}</span>}
        <span title={people.join(", ")} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }}>
          {c.owner?.name ?? "—"}{c.participants.length ? ` +${c.participants.length}` : ""}
        </span>
        {c.due_date && <span style={{ color: tone.due, fontWeight: c.overdue && !done ? 600 : 400 }}>{fmtDue(c.due_date, c.due_time)}</span>}
      </div>
    </div>
  );
}
